import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Send, ClipboardCheck, Mail, MessageSquare, FileText, Calendar, User } from 'lucide-react';
import { Customer } from '../lib/customers';
import { StockItem } from '../lib/stockItems';

// Type alias for better readability - suppliers are customers with is_supplier: true
type Supplier = Customer;
import { useBusiness } from '../contexts/BusinessContext';
import html2pdf from 'html2pdf.js';
import { sendSMS } from '../lib/messaging';
import { sendGmailEmail } from '../lib/email';

interface OrderSummaryPDFModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplier: Supplier;
  items: Array<{
    item: StockItem;
    quantityToOrder: number;
    totalCost: number;
  }>;
  totalAmount: number;
  onSendOrder: () => Promise<boolean> | boolean;
}

export default function OrderSummaryPDFModal({
  isOpen,
  onClose,
  supplier,
  items,
  totalAmount,
  onSendOrder
}: OrderSummaryPDFModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [orderSent, setOrderSent] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [smsError, setSmsError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sendMethod, setSendMethod] = useState<'sms' | 'email' | 'both'>('sms');
  const orderRef = useRef<HTMLDivElement>(null);

  const orderDate = new Date().toLocaleDateString();
  const orderNumber = `PO-${Date.now().toString().slice(-6)}`;

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setOrderSent(false);
      setSmsSent(false);
      setSmsError(null);
      setEmailSent(false);
      setEmailError(null);
      // Auto-select best send method based on available contact info
      if (supplier.email && supplier.phone) {
        setSendMethod('both');
      } else if (supplier.email) {
        setSendMethod('email');
      } else if (supplier.phone) {
        setSendMethod('sms');
      }
    }
  }, [isOpen, supplier.email, supplier.phone]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!orderRef.current) return;
    
    try {
      setIsGeneratingPdf(true);
      
      const element = orderRef.current.cloneNode(true) as HTMLElement;
      
      element.style.padding = '15px';
      element.style.maxWidth = '800px';
      element.style.margin = '0 auto';
      
      const opt = {
        margin: 10,
        filename: `Order_${`${supplier.first_name}_${supplier.last_name}`.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' as 'portrait' | 'landscape'
        }
      };
      
      await html2pdf().from(element).set(opt).save();
      
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const createSmsMessage = () => {
    // Helper function to clean text for SMS (same as email)
    const cleanText = (text: string): string => {
      if (!text) return '';
      return text
        .replace(/[^\x00-\xFF]/g, '') // Remove non-Latin1 characters (keep only 0-255 range)
        .replace(/[""]/g, '"') // Replace smart quotes with regular quotes
        .replace(/['']/g, "'") // Replace smart apostrophes with regular apostrophes
        .replace(/[–—]/g, '-') // Replace em/en dashes with regular hyphens
        .replace(/…/g, '...') // Replace ellipsis with three dots
        .replace(/[®©™]/g, '') // Remove trademark symbols
        .replace(/[°]/g, ' degrees') // Replace degree symbol
        .replace(/[½¼¾]/g, '') // Remove fraction symbols
        .replace(/[§¶]/g, '') // Remove section and paragraph symbols
        .trim();
    };

    let message = `New Order\n`;
    message += `${orderNumber}\n`;
    message += `Date: ${orderDate}\n\n`;
    
    items.forEach(({ item, quantityToOrder }) => {
      const itemName = cleanText(item.name || 'Item');
      message += `-${itemName} - ${quantityToOrder}\n`;
    });
    
    return message;
  };

  const createEmailMessage = () => {
    // Helper function to clean text of problematic characters and ensure Latin1 compatibility
    const cleanText = (text: string): string => {
      if (!text) return '';
      return text
        .replace(/[^\x00-\xFF]/g, '') // Remove non-Latin1 characters (keep only 0-255 range)
        .replace(/[""]/g, '"') // Replace smart quotes with regular quotes
        .replace(/['']/g, "'") // Replace smart apostrophes with regular apostrophes
        .replace(/[–—]/g, '-') // Replace em/en dashes with regular hyphens
        .replace(/…/g, '...') // Replace ellipsis with three dots
        .replace(/[®©™]/g, '') // Remove trademark symbols
        .replace(/[°]/g, ' degrees') // Replace degree symbol
        .replace(/[½¼¾]/g, '') // Remove fraction symbols
        .replace(/[§¶]/g, '') // Remove section and paragraph symbols
        .trim();
    };

    const businessName = cleanText(selectedBusiness?.name || 'Business');
    const supplierName = cleanText(`${supplier.first_name} ${supplier.last_name}`.trim() || 'Supplier');
    const businessAddress = cleanText(selectedBusiness?.address || '');
    const businessPhone = cleanText(selectedBusiness?.phone || '');
          const supplierEmail = cleanText(supplier.email || '');
      const supplierPhone = cleanText(supplier.phone || '');
      const businessNameClean = cleanText(supplier.type === 'business' ? `${supplier.first_name} ${supplier.last_name}`.trim() : '');
    
    // Create simplified but professional email content (guaranteed Latin1 compatible)
    const htmlContent = `
<div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; color: #333;">
    <div style="background: #f8f9fa; padding: 20px; border-bottom: 3px solid #007bff; text-align: center;">
        <h1 style="font-size: 28px; font-weight: bold; color: #007bff; margin: 0;">PURCHASE ORDER</h1>
        <p style="font-size: 16px; color: #666; margin: 5px 0 0 0;">Order #${cleanText(orderNumber)}</p>
    </div>

    <div style="padding: 20px;">
        <table style="width: 100%; margin-bottom: 30px;">
            <tr>
                <td style="width: 50%; vertical-align: top;">
                    <div style="font-weight: bold; color: #007bff; margin-bottom: 10px;">FROM:</div>
                    <div style="font-weight: bold; font-size: 16px; margin-bottom: 5px;">${businessName}</div>
                    <div style="color: #666; font-size: 14px; line-height: 1.4;">
                        ${businessAddress ? businessAddress + '<br>' : ''}
                        ${businessPhone ? 'Phone: ' + businessPhone + '<br>' : ''}
                        Email: ${cleanText(selectedBusiness?.business_domain_email || 'N/A')}
                    </div>
                </td>
                <td style="width: 50%; vertical-align: top;">
                    <div style="font-weight: bold; color: #007bff; margin-bottom: 10px;">TO:</div>
                    <div style="font-weight: bold; font-size: 16px; margin-bottom: 5px;">${supplierName}</div>
                    <div style="color: #666; font-size: 14px; line-height: 1.4;">
                        ${businessNameClean ? businessNameClean + '<br>' : ''}
                        ${supplierEmail ? 'Email: ' + supplierEmail + '<br>' : ''}
                        ${supplierPhone ? 'Phone: ' + supplierPhone : ''}
                    </div>
                </td>
            </tr>
        </table>

        <table style="width: 100%; margin-bottom: 30px;">
            <tr>
                <td style="padding: 8px 0; border-bottom: 1px solid #eee;"><strong>Order Date:</strong></td>
                <td style="padding: 8px 0; border-bottom: 1px solid #eee; text-align: right;">${cleanText(orderDate)}</td>
            </tr>
            <tr>
                <td style="padding: 8px 0; border-bottom: 1px solid #eee;"><strong>Required Date:</strong></td>
                <td style="padding: 8px 0; border-bottom: 1px solid #eee; text-align: right;">As soon as possible</td>
            </tr>
            <tr>
                <td style="padding: 8px 0; border-bottom: 1px solid #eee;"><strong>Payment Terms:</strong></td>
                <td style="padding: 8px 0; border-bottom: 1px solid #eee; text-align: right;">Net 30</td>
            </tr>
        </table>

        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
            <thead>
                <tr style="background: #f8f9fa;">
                    <th style="padding: 12px; text-align: left; border-bottom: 2px solid #007bff; font-weight: bold;">Item Description</th>
                    <th style="padding: 12px; text-align: left; border-bottom: 2px solid #007bff; font-weight: bold;">Unit</th>
                    <th style="padding: 12px; text-align: center; border-bottom: 2px solid #007bff; font-weight: bold;">Quantity</th>
                    <th style="padding: 12px; text-align: right; border-bottom: 2px solid #007bff; font-weight: bold;">Unit Price</th>
                    <th style="padding: 12px; text-align: right; border-bottom: 2px solid #007bff; font-weight: bold;">Total</th>
                </tr>
            </thead>
            <tbody>
                ${items.map(({ item, quantityToOrder, totalCost }) => {
                  const itemName = cleanText(item.name || 'Item');
                  const itemUnit = cleanText(item.unit || 'unit');
                  return `
                    <tr>
                        <td style="padding: 12px; border-bottom: 1px solid #eee;"><strong>${itemName}</strong></td>
                        <td style="padding: 12px; border-bottom: 1px solid #eee;">${itemUnit}</td>
                        <td style="padding: 12px; border-bottom: 1px solid #eee; text-align: center;">${quantityToOrder}</td>
                        <td style="padding: 12px; border-bottom: 1px solid #eee; text-align: right;">$${item.acquisition_cost.toFixed(2)}</td>
                        <td style="padding: 12px; border-bottom: 1px solid #eee; text-align: right;"><strong>$${totalCost.toFixed(2)}</strong></td>
                    </tr>
                  `;
                }).join('')}
            </tbody>
        </table>

        <div style="margin-top: 20px; text-align: right;">
            <div style="font-size: 18px; font-weight: bold; color: #007bff; border-top: 2px solid #007bff; padding-top: 10px; display: inline-block; min-width: 200px;">
                TOTAL AMOUNT: $${totalAmount.toFixed(2)}
            </div>
        </div>

        <div style="margin-top: 40px; padding: 20px; background: #f8f9fa; border-radius: 5px;">
            <div style="color: #666; font-size: 14px; line-height: 1.6;">
                <strong>Please confirm receipt of this purchase order and provide:</strong><br>
                - Estimated delivery date<br>
                - Order confirmation number<br>
                - Any special shipping instructions<br><br>
                
                <strong>Questions?</strong> Contact us at ${businessPhone ? businessPhone : 'the phone number above'}<br>
                Thank you for your continued service and partnership.
            </div>
            
            <div style="margin-top: 20px; font-size: 12px; color: #888;">
                <strong>Terms and Conditions:</strong> This purchase order constitutes a binding agreement. 
                Please process this order promptly and notify us of any delays or changes. 
                All items must meet agreed specifications and quality standards.
            </div>
        </div>
    </div>
</div>`;

    return htmlContent;
  };

  const sendSmsNotification = async () => {
    if (!selectedBusiness?.id || !supplier.phone) {
      console.error('Cannot send SMS: Missing business ID or supplier phone');
      setSmsError('Cannot send SMS: Missing business ID or supplier phone');
      return { success: false, error: 'Missing business ID or supplier phone' };
    }
    
    try {
      const message = createSmsMessage();
      
      console.log('Sending order SMS notification to:', supplier.phone);
      console.log('Message content:', message);
      
      const result = await sendSMS({ 
        businessId: selectedBusiness.id,
        to: supplier.phone,
        message: message
      });
      
      if (result.success) {
        setSmsSent(true);
        setSmsError(null);
        console.log('SMS notification sent successfully');
      } else {
        setSmsError(result.error || 'Failed to send SMS notification');
        console.error('Failed to send SMS notification:', result.error);
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error sending SMS';
      setSmsError(errorMessage);
      console.error('Error sending SMS notification:', error);
      return { success: false, error: errorMessage };
    }
  };

  const sendEmailNotification = async () => {
    if (!selectedBusiness?.id || !supplier.email) {
      console.error('Cannot send email: Missing business ID or supplier email');
      setEmailError('Cannot send email: Missing business ID or supplier email');
      return { success: false, error: 'Missing business ID or supplier email' };
    }
    
    try {
      const emailBody = createEmailMessage();
      const subject = orderNumber;
      
      console.log('Sending order email notification to:', supplier.email);
      console.log('Email subject:', subject);
      
      const result = await sendGmailEmail({ 
        businessId: selectedBusiness.id,
        to: supplier.email,
        subject: subject,
        body: emailBody
      });
      
      if (result.success) {
        setEmailSent(true);
        setEmailError(null);
        console.log('Email notification sent successfully');
      } else {
        setEmailError(result.error || 'Failed to send email notification');
        console.error('Failed to send email notification:', result.error);
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error sending email';
      setEmailError(errorMessage);
      console.error('Error sending email notification:', error);
      return { success: false, error: errorMessage };
    }
  };

  const handleSendOrder = async () => {
    try {
      setIsSubmitting(true);
      
      const result = await onSendOrder();
      
      if (result) {
        setOrderSent(true);
        
        // Send notifications based on selected method
        if (sendMethod === 'sms' && supplier.phone) {
          await sendSmsNotification();
        } else if (sendMethod === 'email' && supplier.email) {
          await sendEmailNotification();
        } else if (sendMethod === 'both') {
          // Send both SMS and email
          const promises = [];
          if (supplier.phone) {
            promises.push(sendSmsNotification());
          }
          if (supplier.email) {
            promises.push(sendEmailNotification());
          }
          await Promise.all(promises);
        }
        
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Error sending order:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[10000]`}
      onClick={onClose}
      style={{
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[700px] backdrop-blur-xl bg-white/80 transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ 
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)'
        }}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header with Mobile-Optimized Design */}
          <div className="relative bg-gray-800 border-b border-gray-700 shadow-lg">
            <div className="flex items-center justify-between px-2 py-2 sm:px-6 sm:py-4">
              <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center shadow-sm">
                  <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base sm:text-lg font-bold text-white truncate">
                    Order Summary
                  </h2>
                  <div className="text-xs sm:text-sm text-gray-300 font-medium">
                    Purchase order #{orderNumber}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-full bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation shadow-sm"
                style={{ minWidth: '32px', minHeight: '32px' }}
              >
                <X className="h-4 w-4 sm:h-6 sm:w-6" />
              </button>
            </div>
          </div>

          {/* Content Section */}
          <div className="flex-1 overflow-y-auto p-2 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="space-y-4 sm:space-y-6">
              {/* Order Document */}
              <div ref={orderRef} className="backdrop-blur-xl bg-white/90 rounded-2xl border border-purple-200/30 p-4 sm:p-6 shadow-lg pdf-document"
                   style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                
                {/* Order Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between mb-6 space-y-4 sm:space-y-0">
                  <div className="backdrop-blur-sm bg-purple-50/50 rounded-2xl p-4"
                       style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                    <h3 className="text-lg font-bold text-purple-800 mb-1">Purchase Order</h3>
                    <p className="text-sm text-purple-600 font-medium">#{orderNumber}</p>
                    <p className="text-sm text-gray-600 flex items-center mt-1">
                      <Calendar className="h-4 w-4 mr-1" />
                      {orderDate}
                    </p>
                  </div>
                  
                  {selectedBusiness && (
                    <div className="text-right backdrop-blur-sm bg-gray-50/50 rounded-2xl p-4"
                         style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                      <p className="text-sm font-bold text-gray-900">{selectedBusiness.name}</p>
                      {selectedBusiness.address && (
                        <p className="text-xs text-gray-600 mt-1">{selectedBusiness.address}</p>
                      )}
                      {selectedBusiness.phone && (
                        <p className="text-xs text-gray-600">{selectedBusiness.phone}</p>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Supplier Information */}
                <div className="mb-6">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center">
                    <User className="h-4 w-4 mr-2 text-purple-600" />
                    Supplier Information
                  </h4>
                  <div className="backdrop-blur-sm bg-purple-50/50 rounded-2xl p-4"
                       style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                    <div className="flex items-center space-x-3">
                      <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-sm font-bold text-white shadow-sm">
                        {supplier.first_name[0]}{supplier.last_name[0]}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{`${supplier.first_name} ${supplier.last_name}`.trim()}</p>
                        {supplier.type === 'business' && (
                          <p className="text-xs text-purple-600 font-medium">Business Contact</p>
                        )}
                        {supplier.email && (
                          <p className="text-xs text-gray-600">{supplier.email}</p>
                        )}
                        {supplier.phone && (
                          <p className="text-xs text-gray-600">{supplier.phone}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Order Items */}
                <div className="mb-6">
                  <h4 className="text-sm font-bold text-gray-900 mb-3">Order Items</h4>
                  <div className="backdrop-blur-sm bg-white/70 rounded-2xl overflow-hidden border border-purple-200/30"
                       style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-purple-50/50">
                          <tr>
                            <th className="px-3 py-3 text-left font-bold text-purple-800">Item</th>
                            <th className="px-3 py-3 text-center font-bold text-purple-800">Unit</th>
                            <th className="px-3 py-3 text-center font-bold text-purple-800">Quantity</th>
                            <th className="px-3 py-3 text-right font-bold text-purple-800">Unit Price</th>
                            <th className="px-3 py-3 text-right font-bold text-purple-800">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-purple-100/50">
                          {items.map(({ item, quantityToOrder, totalCost }) => (
                            <tr key={item.id} className="hover:bg-purple-50/30">
                              <td className="px-3 py-3 text-left font-medium text-gray-900">{item.name}</td>
                              <td className="px-3 py-3 text-center text-gray-600">{item.unit}</td>
                              <td className="px-3 py-3 text-center font-medium text-gray-900">{quantityToOrder}</td>
                              <td className="px-3 py-3 text-right text-gray-600">${item.acquisition_cost.toFixed(2)}</td>
                              <td className="px-3 py-3 text-right font-bold text-purple-700">${totalCost.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-purple-100/50">
                          <tr>
                            <td colSpan={4} className="px-3 py-3 text-right font-bold text-gray-900">Total Amount:</td>
                            <td className="px-3 py-3 text-right font-bold text-purple-800 text-base">${totalAmount.toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
                
                {/* Terms and Conditions */}
                <div className="mt-8 backdrop-blur-sm bg-gray-50/50 rounded-2xl p-4 border border-gray-200/30"
                     style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                  <p className="text-xs font-bold text-gray-700 mb-2">Terms and Conditions:</p>
                  <p className="text-xs text-gray-600">Please process this order promptly. Contact us for any questions or delivery updates.</p>
                </div>
              </div>
              
              {/* Send Method Selection */}
              {(supplier.phone || supplier.email) && (
                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 p-4 sm:p-6 shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <h4 className="text-sm font-bold text-gray-900 mb-4">Send Method</h4>
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                    {supplier.phone && (
                      <label className="flex items-center backdrop-blur-sm bg-white/50 rounded-xl p-3 border border-purple-200/30 cursor-pointer hover:bg-white/70 transition-all duration-200">
                        <input
                          type="radio"
                          value="sms"
                          checked={sendMethod === 'sms'}
                          onChange={(e) => setSendMethod(e.target.value as 'sms')}
                          className="mr-3 text-purple-600"
                        />
                        <MessageSquare className="h-4 w-4 mr-2 text-purple-600" />
                        <span className="font-medium text-gray-800">SMS</span>
                      </label>
                    )}
                    {supplier.email && (
                      <label className="flex items-center backdrop-blur-sm bg-white/50 rounded-xl p-3 border border-purple-200/30 cursor-pointer hover:bg-white/70 transition-all duration-200">
                        <input
                          type="radio"
                          value="email"
                          checked={sendMethod === 'email'}
                          onChange={(e) => setSendMethod(e.target.value as 'email')}
                          className="mr-3 text-purple-600"
                        />
                        <Mail className="h-4 w-4 mr-2 text-purple-600" />
                        <span className="font-medium text-gray-800">Email</span>
                      </label>
                    )}
                    {supplier.phone && supplier.email && (
                      <label className="flex items-center backdrop-blur-sm bg-white/50 rounded-xl p-3 border border-purple-200/30 cursor-pointer hover:bg-white/70 transition-all duration-200">
                        <input
                          type="radio"
                          value="both"
                          checked={sendMethod === 'both'}
                          onChange={(e) => setSendMethod(e.target.value as 'both')}
                          className="mr-3 text-purple-600"
                        />
                        <Send className="h-4 w-4 mr-2 text-purple-600" />
                        <span className="font-medium text-gray-800">Both</span>
                      </label>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={isGeneratingPdf || isSubmitting}
                  className="flex-1 inline-flex justify-center items-center px-6 py-3 text-sm font-bold text-gray-700 bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-gray-500/20 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-lg disabled:opacity-50 disabled:transform-none"
                  style={{ 
                    backdropFilter: 'blur(10px)', 
                    WebkitBackdropFilter: 'blur(10px)',
                    minHeight: '48px'
                  }}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
                </button>
                <button
                  type="button"
                  onClick={handleSendOrder}
                  disabled={isSubmitting || orderSent || isGeneratingPdf || (!supplier.phone && !supplier.email)}
                  className={`flex-1 inline-flex justify-center items-center px-6 py-3 text-sm font-bold text-white rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-lg disabled:opacity-50 disabled:transform-none ${
                    orderSent 
                      ? 'bg-green-600 hover:bg-green-700 shadow-green-500/20' 
                      : 'bg-purple-600 hover:bg-purple-700 shadow-purple-500/20'
                  }`}
                  style={{ minHeight: '48px' }}
                >
                  {orderSent ? (
                    <>
                      <ClipboardCheck className="mr-2 h-4 w-4" />
                      Order Sent
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Send Order {sendMethod === 'both' ? '(SMS & Email)' : sendMethod === 'sms' ? '(SMS)' : '(Email)'}
                    </>
                  )}
                </button>
              </div>
              
              {/* Success Message */}
              {orderSent && (
                <div className="backdrop-blur-xl bg-green-50/90 border border-green-200/50 rounded-2xl p-4 shadow-lg"
                     style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                  <p className="text-sm text-green-700 flex items-center font-medium">
                    <ClipboardCheck className="h-4 w-4 mr-2" />
                    Order sent successfully to {`${supplier.first_name} ${supplier.last_name}`.trim()}!
                  </p>
                  <div className="mt-2 text-xs text-green-600 space-y-1">
                    {smsSent && <div className="flex items-center"><span className="text-green-500 mr-1">✓</span> SMS notification sent to {supplier.phone}</div>}
                    {emailSent && <div className="flex items-center"><span className="text-green-500 mr-1">✓</span> Email notification sent to {supplier.email}</div>}
                  </div>
                </div>
              )}
              
              {/* Error Messages */}
              {(smsError || emailError) && (
                <div className="backdrop-blur-xl bg-yellow-50/90 border border-yellow-200/50 rounded-2xl p-4 shadow-lg"
                     style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                  <p className="text-sm text-yellow-700 flex items-center font-medium">
                    <span className="mr-2">⚠️</span>
                    Order created but some notifications failed:
                  </p>
                  <div className="mt-2 text-xs text-yellow-600 space-y-1">
                    {smsError && <div>SMS failed: {smsError}</div>}
                    {emailError && <div>Email failed: {emailError}</div>}
                  </div>
                </div>
              )}
              
              {/* Message Preview */}
              {(supplier.phone || supplier.email) ? (
                <div className="backdrop-blur-xl bg-blue-50/90 border border-blue-200/50 rounded-2xl p-4 shadow-lg"
                     style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                  <p className="text-sm text-blue-700 font-medium mb-3">
                    Order will be sent via {sendMethod} to supplier: 
                    {sendMethod === 'sms' && ` ${supplier.phone}`}
                    {sendMethod === 'email' && ` ${supplier.email}`}
                    {sendMethod === 'both' && ` ${supplier.phone} (SMS) and ${supplier.email} (Email)`}
                  </p>
                  
                  {/* SMS Preview */}
                  {(sendMethod === 'sms' || sendMethod === 'both') && supplier.phone && (
                    <div className="mt-3 backdrop-blur-sm bg-white/80 rounded-xl border border-blue-200/30 p-3"
                         style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                      <div className="font-bold text-xs text-gray-700 mb-2 flex items-center">
                        <MessageSquare className="h-3 w-3 mr-1" />
                        SMS preview:
                      </div>
                      <pre className="text-xs text-gray-600 font-mono whitespace-pre-wrap break-words bg-gray-50/50 rounded p-2">
                        {createSmsMessage()}
                      </pre>
                    </div>
                  )}
                  
                  {/* Email Preview */}
                  {(sendMethod === 'email' || sendMethod === 'both') && supplier.email && (
                    <div className="mt-3 backdrop-blur-sm bg-white/80 rounded-xl border border-blue-200/30 p-3"
                         style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                      <div className="font-bold text-xs text-gray-700 mb-2 flex items-center">
                        <Mail className="h-3 w-3 mr-1" />
                        Email preview:
                      </div>
                      <div className="text-xs text-gray-600 mb-2">
                        <span className="font-medium">Subject:</span> {orderNumber}
                      </div>
                      <div 
                        className="text-xs bg-white/70 border rounded-lg p-2 max-h-64 overflow-y-auto"
                        dangerouslySetInnerHTML={{ __html: createEmailMessage() }}
                        style={{ fontSize: '10px', lineHeight: '1.2', transform: 'scale(0.8)', transformOrigin: 'top left', width: '125%' }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="backdrop-blur-xl bg-gray-50/90 border border-gray-200/50 rounded-2xl p-4 shadow-lg"
                     style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                  <p className="text-sm text-gray-600">
                    No contact information available for this supplier. Add a phone number or email address in supplier settings to enable notifications.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
} 